<template>
  <div class="relative mb-1 rounded-sm bg-gray-800 px-3 py-2 text-white hover:ring-2 hover:ring-yellow-500 agenda-event"
    :class="{ 'ring-yellow-500 ring-4': selected, 'ring-4 ring-white': eventStatus === 'LIVE' }">
    <div class="flex gap-4">

      <!-- STATUS BAR (full height) -->
      <div class="w-1 self-stretch rounded-full" :class="bgStatusColor(eventStatus)"></div>

      <!-- CONTENT -->
      <div class="min-w-0 flex-1">

        <!-- ROW 1: TIMES + MAIN INFO + OWNER -->
        <div class="flex flex-col md:flex-row md:items-center gap-4">
          <!-- TIMES -->
          <div class="flex gap-4 text-xs">
            <div v-for="col in timeColumns" :key="col.field">
              <div class="text-white/50">{{ col.headerName }}</div>
              <div class="font-semibold">
                {{ event[col.field] | dateUtc('HH:mm') }} <span class="text-white/35 font-normal">{{ event[col.field] | dateUtc('DD.MM') }}</span>
              </div>
            </div>
          </div>

          <!-- MAIN INFO -->
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2">
                 <label class="items-center cursor-pointer sm:inline-flex" :for="event.id + 'x'"
                    :class="{ 'pointer-events-none': !showCheck }">
                    <input type="checkbox" class="mr-2 form-checkbox" v-show="showCheck" :name="event.id + 'x'"
                    :checked="getSelectedIndex(event) !== -1">
                    <span class="truncate text-sm font-semibold" :class="eventStatusTextColorClass">{{ event.name }}</span>
                </label>
                <div v-if="eventProblems.length" class="ml-2 px-1 text-xs font-bold text-red-600 bg-white rounded-sm whitespace-nowrap">
                   PROBLEMS: {{ eventProblems.join(', ') }}
                </div>
            </div>
            <div class="truncate text-xs text-white/45">
               {{ event.description }}
               <!-- Extra columns that are not time, input, or output -->
                <span v-for="col in otherColumns" :key="col.field">
                   <span class="mx-1">·</span>
                   <span v-if="col.type === 'html'" v-html="event[col.field]"></span>
                   <span v-else>{{ event[col.field] }}</span>
                </span>
            </div>
          </div>

          <!-- OWNER / GROUP -->
          <div class="flex shrink-0 items-center gap-1 text-xs">
            <span v-if="event.account && event.account.name" class="rounded-sm bg-black/60 px-2 py-0.5 text-emerald-300">{{ event.account.name }}</span>
            <span v-if="event.customer && event.customer.name" class="rounded-sm bg-black/60 px-2 py-0.5 text-white/70">{{ event.customer.name }}</span>
            <span v-if="event.commentaryLanguage" class="rounded-sm bg-black/60 px-2 py-0.5 text-white/70">
                <i class="fas fa-globe-americas mr-1"></i> {{ event.commentaryLanguage }}
            </span>
          </div>
        </div>

        <!-- ROW 2: INPUTS + OUTPUTS -->
        <div class="mt-2 flex flex-col md:flex-row items-start gap-6">

          <!-- INPUTS -->
          <div class="flex items-center" v-if="hasInputColumn">
             <span
              class="flex items-center gap-1.5 rounded-sm rounded-r-none bg-black/60 px-2 py-2 text-xs font-semibold text-white/55 h-full"
              title="Inputs"
            >
              <!-- icon (plug) -->
              <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" height="24" width="24">
                <g id="slow-motion-video">
                  <path id="Union" fill="currentColor" d="M7.0957 18.3174c1.10652 0.8602 2.44356 1.4359 3.9043 1.6181v2.0137c-2.01144 -0.1997 -3.84745 -0.9944 -5.3291 -2.207zM13 2.0498c5.0533 0.50175 9 4.76487 9 9.9502 0 5.1853 -3.9467 9.4474 -9 9.9492v-2.0137c3.9462 -0.4922 7 -3.856 7 -7.9355 0 -4.07955 -3.0538 -7.44434 -7 -7.93652zM4.06445 13c0.18215 1.4603 0.75741 2.797 1.61719 3.9033l-1.4248 1.4248C3.04464 16.8466 2.24948 15.011 2.0498 13zM16 12l-6 4V8zM5.68164 7.0957C4.8216 8.20212 4.24664 9.53946 4.06445 11H2.0498c0.1997 -2.01126 0.99458 -3.84751 2.20704 -5.3291zM11 4.06348c-1.46058 0.18217 -2.79784 0.75809 -3.9043 1.61816l-1.4248 -1.4248C7.15249 3.04438 8.98874 2.2495 11 2.0498z" stroke-width="1"></path>
                </g>
              </svg>
            </span>

            <div class="flex items-center gap-2">
                <!-- Main Input -->
                 <div class="flex items-center gap-1.5 rounded-sm rounded-l-none bg-black/40 px-2 py-1 h-8" v-if="event.input">
                     <!-- Thumbnail -->
                    <div class="relative group">
                        <img :src="thumbData.data" v-if="thumbData.data && (isSysAdmin || (event.status === 'LIVE'))" class="h-6 w-9 rounded-sm object-cover" />
                        <img :src="thumbData.data" v-if="thumbData.data && (isSysAdmin || (event.status === 'LIVE'))" class="absolute z-50 hidden w-52 group-hover:block top-0 left-0" style="max-width: none;" />
                    </div>

                    <span class="text-xs font-medium" v-html="event.input.name"></span>
                    <!-- Alarms Badge -->
                     <span :class="[bgInputStatusColor]" @click.stop="showModal = true" v-if="inputAlert.length > 0"
                        class="px-1.5 rounded-full text-[10px] flex items-center text-center justify-center cursor-pointer text-white h-4 bg-red-500">
                        {{ inputAlert.length }}
                    </span>
                     <span :class="[bgInputStatusColor]" v-if="inputAlert.length === 0 && inputStatusData.status"
                        class="w-2 h-2 rounded-full inline-block">
                    </span>
                 </div>
            </div>
          </div>


          <!-- OUTPUTS -->
          <div class="flex min-w-0 flex-1 items-start">
            <!-- icon instead of "Outputs" -->
            <span
              class="flex items-center gap-1.5 rounded-sm rounded-r-none bg-black/60 px-2 py-2 text-xs font-semibold text-white/55 h-full"
              title="Outputs"
            >
              <!-- icon (arrow out) -->
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" height="24" width="24">
                <g id="output">
                  <path id="Union" fill="currentColor" d="M20.1025 3.00488C20.6067 3.05621 21 3.48232 21 4v3h-2V5H5v14h14v-2h2v3c0 0.5523 -0.4477 1 -1 1H4c-0.55228 0 -1 -0.4477 -1 -1V4l0.00488 -0.10254C3.05621 3.39333 3.48232 3 4 3h16zM21.207 11.293l0.0684 0.0761c0.3203 0.3928 0.2977 0.9718 -0.0684 1.3379l-4 4 -1.414 -1.414L18.0859 13H9v-2h9.0859L15.793 8.70703l1.414 -1.41406z" stroke-width="1"></path>
                </g>
              </svg>
            </span>

             <div class="flex min-w-0 flex-wrap gap-1.5">
                <div v-for="output in outputsList" :key="output.id"
                     class="flex items-center gap-1 rounded-sm rounded-l-none bg-black/40 px-2 py-1 text-xs h-8">
                    <span class="font-medium">{{ output.displayName || output.name }}</span>
                    
                     <span class="rounded-full px-1.5 text-[10px]"
                        :class="'output-type-tag ' + output.displayLabel"
                     >{{ output.displayLabel }}</span>

                     <span class="rounded-full px-1.5 text-[10px]"
                        :class="'output-type-tag output-status-' + output.status"
                     >{{ output.status }}</span>

                     <!-- Stream Key / Autostart -->
                     <span v-if="output.hasStreamKey">
                         <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-green-500">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
                        </svg>
                     </span>
                      <span v-else-if="output.hasWowzaConfig">
                           <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-red-500">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
                            </svg>
                      </span>

                      <span v-if="output.hasWowzaConfig && output.autostart" title="Autostart ON">
                          <ma-time class="bly-time green" :title="`Offset ${output.autostartOffsetMin} min`"></ma-time>
                      </span>
                       <span v-if="output.hasWowzaConfig && !output.autostart" title="Autostart OFF">
                          <ma-time class="bly-time red"></ma-time>
                      </span>
                      
                      <!-- OTT -->
                      <span v-if="output.ottServerString" v-html="output.ottServerString"></span>

                      <!-- Connection Status -->
                      <span v-if="output.isConnected !== null" class="w-2 h-2 rounded-full"
                        :class="output.isConnected ? 'bg-green-500' : 'bg-red-500'"></span>
                      
                      <!-- Override -->
                       <span v-if="output.override" class="show-ma text-[10px] text-amber-200" >(OTT: {{output.override}})</span>

                </div>
                <div 
                     class="flex items-center gap-1 rounded-sm rounded-l-none bg-black/40 px-2 py-1 text-xs h-8">
                    <span v-if="outputsList.length === 0" class="text-xs text-white/45">No outputs assigned</span>
                </div>
             </div>

          </div>

          <!-- AUDIO -->
          <div class="flex items-start" v-if="hasAudioColumn && audioInfo">
            <span
              class="flex items-center gap-1.5 rounded-sm rounded-r-none bg-black/60 px-2 py-2 text-xs font-semibold text-white/55 h-full"
              title="Audio"
            >
              <!-- icon (speaker) -->
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-white/55" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              </svg>
            </span>
            <div class="flex items-center gap-1.5 rounded-sm rounded-l-none bg-black/40 px-2 py-1 text-xs min-h-8">
               <pre class="font-sans audio text-[10px]">{{ audioInfo }}</pre>
            </div>
          </div>


        </div>

      </div>
    </div>
    
    <!-- ACTIONS -->
    <div class="absolute bottom-0 right-0 bg-gray-800 agenda-event-actions">
        <!-- This slot is where buttons/actions are injected -->
        <slot></slot>
    </div>

    <!-- MODAL (Copied from AgendaItem.vue) -->
    <div v-show="showModal" class="fixed inset-0 z-10 overflow-y-auto">
      <div class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 transition-opacity" @click="showModal = false">
          <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div
          class="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">

            <h4 class="font-bold text-gray-900">Alarms</h4>
            <div class="sm:flex sm:items-start">
              <div class="mt-3 text-center sm:mt-0 sm:text-left max-h-[70vh] w-full overflow-y-scroll">
                <ul>
                  <li v-for="(alert, index) in inputAlert" :key="index" class="py-2 border-b border-gray-200 text-gray-700">
                    <div class="text-xs">{{ alert.level }} &bull; {{ alert.type }} &bull; {{ new
                      Date(alert.trigger_time).toLocaleString()}}</div>
                    <div>{{ alert.name }}</div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="px-4 py-3 bg-gray-50 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-sm shadow-xs sm:ml-3 sm:w-auto">
              <button @click="showModal = false"
                class="inline-flex justify-center w-full px-4 py-2 text-base font-medium leading-6 text-white transition duration-150 ease-in-out bg-indigo-600 border border-transparent rounded-sm shadow-xs hover:bg-indigo-500 focus:outline-hidden focus:border-indigo-700 focus:shadow-outline-indigo sm:text-sm sm:leading-5">
                Close
              </button>
            </span>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import moment from 'moment'

const props = defineProps({
  event: {
    type: Object,
    required: true
  },
  groupEventsStatuses: {
    type: Array,
    default: () => []
  },
  visibleColumnDefs: {
    type: Array,
    default: () => []
  },
  isMobileOnly: {
    type: Boolean,
    default: false
  },
  showCheck: {
    type: Boolean,
    default: false
  },
  selectedEvents: {
    type: Array,
    default: () => []
  },
  selected: {
    type: Boolean,
    default: false
  },
  serverStatus: {
    type: Object,
    default: () => ({})
  },
  inputStatuses: {
    type: Object,
    default: () => ({})
  },
  isSysAdmin: {
    type: Boolean,
    default: false
  },
  streamManagerConnections: {
    type: Object,
    default: () => ({})
  }
})

// Reactive data
const showModal = ref(false)
const thumb = ref({})
const thumbAll = ref({
  input: {},
  override: {}
})

// Columns helper
const timeColumns = computed(() => {
    return props.visibleColumnDefs.filter(col => col.type === 'time');
});

const otherColumns = computed(() => {
    return props.visibleColumnDefs.filter(col => col.type !== 'time' && col.field !== 'inputName' && col.field !== 'outputUI' && col.field !== 'audioUI');
});

const hasInputColumn = computed(() => {
    return props.visibleColumnDefs.some(col => col.field === 'inputName');
});

const hasAudioColumn = computed(() => {
    return props.visibleColumnDefs.some(col => col.field === 'audioUI');
});

const audioInfo = computed(() => {
    return props.event.audioUI || '';
});


// Computed properties
const groupStatuses = computed(() => {
  return [...new Set(props.groupEventsStatuses)]
})

const thumbData = computed(() => {
  const { input, inputOverride } = props.event
  if (inputOverride && inputOverride.name && inputOverride?.probeProjectId) {
    return props.inputStatuses[inputOverride?.probeProjectId][inputOverride.probeTaskId]?.thumb || {}
  }
  if (input && !input.probeProjectId && props.inputStatuses[input.id] && props.inputStatuses[input.id].thumb) {
    return props.inputStatuses[input.id].thumb || {}
  }
  if (!props.inputStatuses || !props.inputStatuses[input?.probeProjectId]) return {}
  return props.inputStatuses[input?.probeProjectId][input?.probeTaskId]?.thumb || {}
})

const inputStatusData = computed(() => {
  const { input, inputOverride } = props.event
  if (inputOverride && inputOverride.name && inputOverride.probeTaskId && props.inputStatuses && props.inputStatuses[input?.probeProjectId] && props.inputStatuses[input.probeProjectId][inputOverride.probeTaskId]) {
    thumb.value = props.inputStatuses[inputOverride?.probeProjectId][inputOverride?.probeTaskId].thumb || {}
    thumbAll.value.override = props.inputStatuses[inputOverride?.probeProjectId][inputOverride?.probeTaskId].thumb || {}
    return thumb.value
  }
  if (input && !input.probeProjectId && props.inputStatuses[input.id]) {
    thumb.value = props.inputStatuses[input.id].thumb || {}
    thumbAll.value.input = props.inputStatuses[input.id].thumb || {}
    return props.inputStatuses[input.id] || {}
  }
  if (!props.inputStatuses || !input || !props.inputStatuses || !props.inputStatuses[input?.probeProjectId] || !input?.probeTaskId) return {}
  
  if (props.inputStatuses[input?.probeProjectId] && props.inputStatuses[input?.probeProjectId][input?.probeTaskId]) {
    thumb.value = props.inputStatuses[input?.probeProjectId][input?.probeTaskId].thumb || {}
    thumbAll.value.input = props.inputStatuses[input?.probeProjectId][input?.probeTaskId].thumb || {}
  }
  return props.inputStatuses[input?.probeProjectId][input?.probeTaskId] || {}
})

const inputAlert = computed(() => {
  return inputStatusData.value.alarms || []
})

const bgInputStatusColor = computed(() => {
  if (!inputStatusData.value.status) return 'bg-neutral-500'
  if (inputAlert.value.length > 0) return 'bg-red-500'
  return 'bg-green-500'
})

const eventStatus = computed(() => {
  return props.event.status
})

const eventStatusTextColorClass = computed(() => {
  return `text-${eventStatus.value}`
})

const bgStatusColor = computed(() => {
  return status => `bg-${status}`
})


const outputsList = computed(() => {
    const list = [];
    if (!props.event.outputs || !props.event.outputs.items) return list;

    props.event.outputs.items
    .filter((output) => !!output)
    .forEach((el) => {
        const item = { ...el.output };
        // Find matching action config
        const [outputConfig] = (props.event.actions && props.event.actions.items)
            ? props.event.actions.items.filter((action) => action.assignedOutput && action.assignedOutput.id === el.output.id)
            : [null];

        const hasWowzaConfig = outputConfig && outputConfig.configWowza;
        item.hasWowzaConfig = !!hasWowzaConfig;
        
        if (hasWowzaConfig) {
             item.autostart = outputConfig.configWowza.autostart;
             item.autostartOffsetMin = outputConfig.configWowza.autostartOffsetMin || 0;
             item.hasStreamKey = outputConfig.configWowza.targets && outputConfig.configWowza.targets.items && outputConfig.configWowza.targets.items[0] && outputConfig.configWowza.targets.items[0].streamKey;
        }

        const statusData = statusByName(el.output);
        item.status = statusData.status || 'Unknown'; // Active, NotFound, Error
        
        item.isConnected = null;
        if (props.isSysAdmin && el.output.smEndpoints) {
            try {
                const t = JSON.parse(el.output.smEndpoints);
                const ids = t.map(sm => sm.interfaceId);
                let connected = false;
                // Simply check if we have connection data
                 ids.forEach(id => {
                    if (props.streamManagerConnections && props.streamManagerConnections[id] && props.streamManagerConnections[id].outputs_connected !== '') {
                        connected = true;
                    }
                    // This logic from AgendaItem is a bit confusing but essentially checking specific conditions
                 });
                 // Simplified logic as per AgendaItem visual result:
                 // if connected -> green span
                 // if connected is empty -> red span?
                 // In AgendaItem: 
                 // isConnectedString = '<span class="w-2 h-2 bg-green-500 inline-block"></span>' if outputs_connected !== ''
                 // isConnectedString = '<span class="w-2 h-2 bg-red-500 inline-block"></span>' if outputs_connected === '' (and connection exists)
                 // <span class="w-2 h-2 bg-slate-500 inline-block"></span> if !connection[id]
                 
                 // Replicating one found connection state for the item:
                 const connection = ids.map(id => props.streamManagerConnections ? props.streamManagerConnections[id] : null).find(c => c);
                 if (connection) {
                     item.isConnected = (connection.outputs_connected !== '');
                 } else {
                     item.isConnected = null; // Slate color in old logic
                 }
                 
            } catch(e) {
                console.error(e);
            }
        }

        item.ottServerString = el.output.ottServer && el.output.ottApplication ? `(${el.output.ottServer} - ${el.output.ottApplication})` : '';
        item.override = el.outputOttApplicationOverride;
        item.id = el.output.id;

        item.displayLabel = item.outputType;
        if (item.outputType === 'TECHEX') {
            if (item.url && item.url.startsWith('srt')) item.displayLabel = 'CING-SRT';
            else if (item.url && item.url.startsWith('rtp')) item.displayLabel = 'CING-RTP';
            else item.displayLabel = 'CING';
        }

        list.push(item);
    });
    return list;
});


// Methods
const getSelectedIndex = (event) => {
  let index = -1
  if (!props.selectedEvents) return index
  props.selectedEvents.forEach((ev, i) => {
    if (event.id === ev.id) index = i
  })
  return index
}

const statusByName = (output) => {
  const status = props.serverStatus[output.name]
  if (!status) return {}
  return status || {}
}

const eventProblems = computed(() => {
  if (!props.isSysAdmin) return []
  const problems = []
  
  if (!props.event.actions || !props.event.actions.items || props.event.actions.items.length === 0) {
    problems.push('ACTIONS')
  }
  if (!props.event.start) {
    problems.push('DATE')
  }
  if (!props.event.input) {
    problems.push('INPUT')
  }
  if (!props.event.outputs || !props.event.outputs.items || props.event.outputs.items.length === 0) {
    problems.push('OUTPUTS')
  }
  
  if (props.event.lineupTime && props.event.startTime && moment(props.event.lineupTime).isAfter(moment(props.event.startTime))) {
    problems.push('LINEUP > START')
  }
  if (props.event.startTime && props.event.endTime && moment(props.event.startTime).isAfter(moment(props.event.endTime))) {
    problems.push('START > END')
  }

  return problems
})


</script>

<style>
.agenda-event-actions {
  display: none;
}

.agenda-event:hover .agenda-event-actions {
  display: block;
}
</style>
